import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import { shopifyService } from '../services/shopifyService';
import { toast } from 'sonner';
import { Link, RefreshCw, CheckCircle, XCircle, ExternalLink, Trash2, AlertTriangle, Store, Shield } from 'lucide-react';
import TeamAccessSection, { PendingInvitesBanner } from '../components/TeamAccessSection';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';  // bare origin; endpoints carry /api

/**
 * MerchantSettings
 * 
 * UPDATED for Shopify App Store Compliance:
 * - Removed "Custom App" creation instructions (violates 2.2.1)
 * - Removed manual API token input fields
 * - Now directs users to install via Shopify App Store for proper OAuth
 * 
 * This page is for the EXTERNAL merchant portal settings.
 */
export default function MerchantSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shopifyStatus, setShopifyStatus] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [mockupStatus, setMockupStatus] = useState(null);
  const [clearingMockup, setClearingMockup] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const teamFetch = useCallback(async (endpoint, options = {}) => {
    const token = sessionStorage.getItem('session_token') || localStorage.getItem('ci_session_token');
    const res = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...(options.headers || {}) },
    });
    if (!res.ok) throw new Error(`Request failed: ${endpoint}`);
    return res.json();
  }, []);

  useEffect(() => {
    loadSettingsData();
  }, []);

  const loadSettingsData = async () => {
    try {
      const [profileData, statusData, logsData, mockupData] = await Promise.all([
        authService.getProfile(),
        shopifyService.getStatus().catch(() => ({ connected: false })),
        shopifyService.getSyncLogs().catch(() => ({ logs: [] })),
        shopifyService.getMockupStatus().catch(() => ({ has_mockup_data: false }))
      ]);

      setUser(profileData.member);
      setShopifyStatus(statusData);
      setSyncLogs(logsData.logs || []);
      setMockupStatus(mockupData);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load settings:', error);
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    sessionStorage.clear();
    navigate('/login');
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect your Shopify store?')) {
      return;
    }
    
    try {
      await shopifyService.disconnect();
      toast.success('Shopify store disconnected');
      loadSettingsData();
    } catch (error) {
      toast.error('Failed to disconnect', {
        description: error.response?.data?.detail
      });
    }
  };

  const handleManualSync = async () => {
    try {
      setSyncing(true);
      await shopifyService.triggerSync();
      toast.success('Sync started', {
        description: 'Products are being synced in the background'
      });
      
      setTimeout(() => {
        loadSettingsData();
        setSyncing(false);
      }, 3000);
    } catch (error) {
      setSyncing(false);
      toast.error('Failed to start sync', {
        description: error.response?.data?.detail
      });
    }
  };

  const handleClearMockupData = async () => {
    try {
      setClearingMockup(true);
      const result = await shopifyService.clearMockupData();
      
      toast.success('Demo data cleared!', {
        description: `Removed ${result.products_deleted} products and ${result.deals_deleted} deals`
      });
      
      setShowClearConfirm(false);
      loadSettingsData();
    } catch (error) {
      toast.error('Failed to clear demo data', {
        description: error.response?.data?.detail || 'Please try again'
      });
    } finally {
      setClearingMockup(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      {/* Header */}
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Merchant Settings</h1>
            <p className="text-sm text-[#9CB0C7]">Settings & Integrations</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors"
            >
              ← Back to Dashboard
            </button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name || user?.email}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-white mb-8">Integrations</h2>

        {/* Shopify Integration Card */}
        <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 shadow-[0_0_0_1px_rgba(62,241,199,0.06)] mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#96bf48]/10 rounded-lg flex items-center justify-center">
                <Store className="w-8 h-8 text-[#96bf48]" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-1">Shopify Store Integration</h3>
                <p className="text-sm text-[#9CB0C7]">
                  Connect your Shopify store to automatically sync products and inventory
                </p>
              </div>
            </div>
            
            {shopifyStatus?.connected && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#8CF78E]/10 border border-[#8CF78E]/20 rounded-lg">
                <CheckCircle className="w-4 h-4 text-[#8CF78E]" />
                <span className="text-sm font-medium text-[#8CF78E]">Connected</span>
              </div>
            )}
          </div>

          {shopifyStatus?.connected ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-[#111A2E] rounded-lg">
                <div>
                  <p className="text-xs text-[#9CB0C7] mb-1">Store Name</p>
                  <p className="text-sm font-medium text-white">{shopifyStatus.shop_name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#9CB0C7] mb-1">Store URL</p>
                  <p className="text-sm font-medium text-white">{shopifyStatus.store_url}</p>
                </div>
                <div>
                  <p className="text-xs text-[#9CB0C7] mb-1">Connected At</p>
                  <p className="text-sm font-medium text-white">
                    {shopifyStatus.connected_at ? new Date(shopifyStatus.connected_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#9CB0C7] mb-1">Last Sync</p>
                  <p className="text-sm font-medium text-white">
                    {shopifyStatus.last_sync_at ? new Date(shopifyStatus.last_sync_at).toLocaleString() : 'Never'}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleManualSync}
                  disabled={syncing}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90 transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="sync-now-button"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </button>
                <button
                  onClick={handleDisconnect}
                  className="px-4 py-2.5 rounded-lg border border-[#F55454] text-[#F55454] hover:bg-[#F55454]/10 transition-colors font-semibold"
                >
                  Disconnect
                </button>
              </div>

              {/* Sync Logs */}
              {syncLogs.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-white mb-3">Recent Sync History</h4>
                  <div className="space-y-2">
                    {syncLogs.slice(0, 5).map((log) => (
                      <div key={log._id} className="p-3 bg-[#111A2E] rounded-lg border border-[#22304A]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-[#9CB0C7]">
                            {new Date(log.started_at).toLocaleString()}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            log.status === 'completed' ? 'bg-[#8CF78E]/10 text-[#8CF78E]' :
                            log.status === 'failed' ? 'bg-[#F55454]/10 text-[#F55454]' :
                            'bg-[#FFC857]/10 text-[#FFC857]'
                          }`}>
                            {log.status}
                          </span>
                        </div>
                        {log.status === 'completed' && (
                          <p className="text-xs text-[#9CB0C7]">
                            Synced {log.products_synced} products ({log.products_created} new, {log.products_updated} updated)
                          </p>
                        )}
                        {log.error && (
                          <p className="text-xs text-[#F55454] mt-1">{log.error}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Not connected - Direct to Shopify App Store */
            <div className="text-center py-8">
              <p className="text-[#9CB0C7] mb-6">
                Connect your Shopify store to automatically import products, sync inventory, and update pricing in real-time.
              </p>
              
              {/* Install from Shopify App Store - Compliant Method */}
              <a
                href="https://apps.shopify.com/commerce-index"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 rounded-lg font-semibold text-white transition-colors flex items-center justify-center gap-2 mx-auto max-w-xs"
                style={{background: '#95BF47'}}
                data-testid="install-shopify-button"
              >
                <Store className="w-5 h-5" />
                Install from Shopify App Store
                <ExternalLink className="w-4 h-4" />
              </a>
              
              <div className="flex items-center gap-2 justify-center text-sm text-[#9CB0C7] mt-4">
                <Shield className="w-4 h-4 text-[#3EF1C7]" />
                <span>Secure OAuth connection via Shopify</span>
              </div>
              
              <p className="text-xs text-[#9CB0C7] mt-6">
                After installation, your store will automatically connect and sync.
              </p>
            </div>
          )}
        </div>

        {/* Demo Data Management Card */}
        {mockupStatus?.has_mockup_data && (
          <div className="rounded-lg border border-[#FFC857]/30 bg-[#0E1426]/80 backdrop-blur p-6 shadow-[0_0_0_1px_rgba(255,200,87,0.1)] mb-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#FFC857]/10 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-[#FFC857]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Demo Data Active</h3>
                  <p className="text-sm text-[#9CB0C7]">
                    Your account currently has sample demo data for testing purposes.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-[#111A2E] rounded-lg mb-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-[#FFC857]">{mockupStatus.mockup_products || 0}</p>
                  <p className="text-xs text-[#9CB0C7]">Demo Products</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#FFC857]">{mockupStatus.mockup_deals || 0}</p>
                  <p className="text-xs text-[#9CB0C7]">Demo Deals</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#3EF1C7]">{mockupStatus.real_products || 0}</p>
                  <p className="text-xs text-[#9CB0C7]">Real Products</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 p-3 bg-[#FFC857]/5 border border-[#FFC857]/20 rounded-lg">
                <p className="text-xs text-[#9CB0C7]">
                  <span className="text-[#FFC857] font-medium">Tip:</span> Demo data will be automatically removed when you connect your Shopify store.
                </p>
              </div>
              
              {!showClearConfirm ? (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="px-4 py-2.5 rounded-lg border border-[#F55454]/50 text-[#F55454] hover:bg-[#F55454]/10 transition-colors font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                  data-testid="clear-demo-data-button"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear Demo Data
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    className="px-4 py-2.5 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors"
                    disabled={clearingMockup}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleClearMockupData}
                    disabled={clearingMockup}
                    className="px-4 py-2.5 rounded-lg bg-[#F55454] text-white hover:bg-[#F55454]/90 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="confirm-clear-demo-button"
                  >
                    {clearingMockup ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Clearing...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Confirm Clear
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Other Integrations Placeholder */}
        <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 shadow-[0_0_0_1px_rgba(62,241,199,0.06)]">
          <h3 className="text-xl font-semibold text-white mb-4">Coming Soon</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-[#111A2E] rounded-lg border border-[#22304A] opacity-50">
              <h4 className="font-semibold text-white mb-2">WooCommerce</h4>
              <p className="text-xs text-[#9CB0C7]">Connect your WooCommerce store</p>
            </div>
            <div className="p-4 bg-[#111A2E] rounded-lg border border-[#22304A] opacity-50">
              <h4 className="font-semibold text-white mb-2">Amazon Seller Central</h4>
              <p className="text-xs text-[#9CB0C7]">Import products from Amazon</p>
            </div>
            <div className="p-4 bg-[#111A2E] rounded-lg border border-[#22304A] opacity-50">
              <h4 className="font-semibold text-white mb-2">BigCommerce</h4>
              <p className="text-xs text-[#9CB0C7]">Sync with BigCommerce catalog</p>
            </div>
            <div className="p-4 bg-[#111A2E] rounded-lg border border-[#22304A] opacity-50">
              <h4 className="font-semibold text-white mb-2">Etsy</h4>
              <p className="text-xs text-[#9CB0C7]">Import Etsy shop products</p>
            </div>
          </div>
        </div>
              <PendingInvitesBanner fetchWithAuth={teamFetch} onChanged={() => {}} />
        <TeamAccessSection merchantId={user?.id || user?.merchant_id} currentRole="owner" fetchWithAuth={teamFetch} />
      </main>
    </div>
  );
}
